/* ============================================================================
 * TemplateShowcase - high-fidelity homepage mockups of the six Instant Site
 * templates, framed in a browser chrome.
 * ============================================================================
 * Each mockup reproduces the REAL template design system from prostay-app
 * (src/instant-site/templates/<id>.css + model.js presets): exact accent
 * colors, font pairings, header/hero/rooms/footer variants. Photography is
 * bundled under /public/images/templates/.
 *
 * The mockups are decorative artwork (aria-hidden, pointer-events-none);
 * microcopy inside them is intentionally not translated, exactly like the
 * fictional hotel names.
 *
 * Design tokens per template (source of truth: prostay-app):
 *   classic     ivory #fbf8f2, brass #9c7c4a, Cormorant Garamond, radius 0
 *   modern      white, indigo #4f46e5, ink #0b1120, Sora, radius 18px
 *   editorial   #f6f3ee panels, clay #b0573a, Playfair Display, radius 0
 *   minimal     #fafaf8, ink #111, Manrope, radius 0, hairlines
 *   resort      cream #fff7ef, coral #ff6b3d -> amber #ffb24a, teal #0f3d3e
 *   futuristic  #070a16, violet #7c5cff -> cyan #4dd0ff, Space Grotesk
 * ========================================================================= */

import React from 'react';
import Image from 'next/image';
import {
  cormorant,
  playfair,
  sora,
  manrope,
  spaceGrotesk,
  jetbrainsMono
} from './fonts';

const IMG = '/images/templates';

/* Browser chrome frame ------------------------------------------------------ */

function BrowserFrame({domain, children, dark = false}) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none select-none overflow-hidden rounded-xl border shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)] ${
        dark ? 'border-white/10 bg-[#0b0e1e]' : 'border-border bg-white'
      }`}
    >
      <div
        className={`flex items-center gap-2 border-b px-3 py-2 ${
          dark ? 'border-white/10 bg-[#0d1124]' : 'border-border-subtle bg-background-subtle'
        }`}
      >
        <span className="flex gap-1.5">
          <span className="size-2 rounded-full bg-[#ff5f57]" />
          <span className="size-2 rounded-full bg-[#febc2e]" />
          <span className="size-2 rounded-full bg-[#28c840]" />
        </span>
        <span
          className={`mx-auto flex items-center gap-1.5 rounded-md px-3 py-0.5 text-[9px] tracking-wide ${
            dark ? 'bg-white/5 text-gray-400' : 'bg-white text-gray-500 ring-1 ring-border-subtle'
          }`}
        >
          <svg viewBox="0 0 10 12" className="h-2 w-1.5" fill="none" aria-hidden>
            <rect x="1" y="5" width="8" height="6" rx="1" stroke="currentColor" />
            <path d="M3 5V3.5a2 2 0 1 1 4 0V5" stroke="currentColor" />
          </svg>
          {domain}
        </span>
      </div>
      {children}
    </div>
  );
}

/* Tiny shared bits ----------------------------------------------------------- */

function Photo({src, alt = '', className = '', sizes = '640px'}) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      <Image src={src} alt={alt} fill sizes={sizes} className="object-cover" />
    </div>
  );
}

/* ============================================================================
 * CLASSIC - heritage luxury. Centered serif wordmark, nav row below,
 * brass hairlines, square bordered room plates, dark footer.
 * ========================================================================= */

function ClassicShowcase() {
  return (
    <BrowserFrame domain="hotelaurelia.com">
      <div className={`bg-[#fbf8f2] text-[#3d3e36] ${cormorant.className}`}>
        {/* Centered header with nav row below */}
        <div className="px-5 pt-4 pb-2 text-center">
          <p className="text-[17px] font-semibold tracking-[0.08em] text-[#23241f]">
            Hotel Aurelia
          </p>
          <p className="font-sans text-[6px] tracking-[0.34em] text-[#9c7c4a] uppercase">
            Grand lac &middot; est. 1912
          </p>
        </div>
        <div className="mx-5 flex items-center justify-center gap-4 border-y border-[#e4ddcd] py-1.5 font-sans text-[6.5px] tracking-[0.22em] text-[#837c6c] uppercase">
          <span>Rooms</span>
          <span>Dining</span>
          <span>Spa</span>
          <span>Stories</span>
          <span className="bg-[#9c7c4a] px-2.5 py-1 font-medium text-white">Book now</span>
        </div>
        {/* Tall centered hero */}
        <div className="relative">
          <Photo src={`${IMG}/tpl-classic-hero.webp`} className="aspect-[16/7.2]" />
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/25 px-6 text-center">
            <p className="font-sans text-[6px] tracking-[0.34em] text-white/80 uppercase">
              Lakeside since 1912
            </p>
            <p className="mt-1.5 max-w-[26ch] text-[19px] leading-snug font-medium text-white">
              A quiet grandeur, kept exactly as you remember it.
            </p>
            <span className="mt-2 block h-px w-12 bg-[#c9a86a]" />
            <span className="mt-2.5 border border-white/70 px-3.5 py-1.5 font-sans text-[6.5px] tracking-[0.26em] text-white uppercase">
              Reserve a stay
            </span>
          </div>
        </div>
        {/* Availability bar */}
        <div className="mx-5 -mt-3 relative z-10 grid grid-cols-[1fr_1fr_1fr_auto] items-stretch border border-[#e4ddcd] bg-white font-sans text-[6.5px]">
          {['Arrival · 12 Jun', 'Departure · 15 Jun', 'Guests · 2 adults'].map((f) => (
            <span key={f} className="border-r border-[#e4ddcd] px-2.5 py-2 tracking-[0.08em] text-[#837c6c] uppercase">
              {f}
            </span>
          ))}
          <span className="flex items-center bg-[#9c7c4a] px-3.5 tracking-[0.22em] text-white uppercase">
            Check rates
          </span>
        </div>
        {/* Rooms - square bordered plates */}
        <div className="px-5 pt-4 pb-1 text-center">
          <p className="font-sans text-[6px] tracking-[0.3em] text-[#9c7c4a] uppercase">Rooms &amp; suites</p>
        </div>
        <div className="grid grid-cols-3 gap-2.5 px-5 pb-4">
          {[
            ['Lake Room', '€240', 'tpl-room-1'],
            ['Garden Junior Suite', '€340', 'tpl-room-2'],
            ['Aurelia Suite', '€520', 'tpl-room-3']
          ].map(([name, price, img]) => (
            <div key={name} className="border border-[#e4ddcd] bg-white">
              <Photo src={`${IMG}/${img}.webp`} className="aspect-[4/3]" sizes="220px" />
              <div className="px-2 py-1.5">
                <p className="text-[9.5px] font-semibold text-[#23241f]">{name}</p>
                <p className="font-sans text-[6px] tracking-[0.1em] text-[#837c6c] uppercase">
                  From <span className="text-[#9c7c4a]">{price}</span> / night
                </p>
              </div>
            </div>
          ))}
        </div>
        {/* Dark footer strip */}
        <div className="flex items-center justify-between bg-[#23241f] px-5 py-2.5">
          <p className="text-[9px] font-medium tracking-[0.08em] text-[#d8d2c2]">Hotel Aurelia</p>
          <p className="font-sans text-[5.5px] tracking-[0.24em] text-[#837c6c] uppercase">
            Rooms &middot; Dining &middot; Spa &middot; Contact
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ============================================================================
 * MODERN - contemporary boutique. Glassy header, hero with floating
 * booking card, rounded row cards, dark CTA band.
 * ========================================================================= */

function ModernShowcase() {
  return (
    <BrowserFrame domain="novahotel.com">
      <div className={`bg-white text-[#232a3b] ${sora.className}`}>
        {/* Glassy standard header */}
        <div className="flex items-center justify-between border-b border-[#e9ebf3] bg-white/85 px-5 py-2.5">
          <p className="text-[11px] font-extrabold tracking-tight text-[#0b1120]">NOVA</p>
          <div className="flex items-center gap-3 font-sans text-[6.5px] font-medium text-[#6b7280]">
            <span>Rooms</span>
            <span>Neighbourhood</span>
            <span>Eat &amp; drink</span>
            <span className="rounded-[8px] bg-[#4f46e5] px-2.5 py-1 font-bold text-white">
              Book now
            </span>
          </div>
        </div>
        {/* Hero with bottom-left copy + floating booking card */}
        <div className="relative">
          <Photo src={`${IMG}/tpl-modern-hero.webp`} className="aspect-[16/7.4]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/15 to-transparent" />
          <div className="absolute bottom-5 left-5 max-w-[52%]">
            <span className="rounded-full bg-white/15 px-2 py-0.5 font-sans text-[5.5px] font-semibold tracking-[0.18em] text-white uppercase backdrop-blur">
              Downtown &middot; 84 rooms
            </span>
            <p className="mt-1.5 text-[17px] leading-tight font-extrabold tracking-tight text-white">
              Sleep in the middle of everything.
            </p>
          </div>
        </div>
        {/* Floating booking card */}
        <div className="relative z-10 mx-5 -mt-4 grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2 rounded-[14px] bg-white p-2.5 shadow-[0_16px_40px_-16px_rgba(15,23,42,0.35)] ring-1 ring-[#e9ebf3]">
          {[
            ['Check-in', 'Jun 12'],
            ['Check-out', 'Jun 15'],
            ['Guests', '2 adults']
          ].map(([label, value]) => (
            <div key={label} className="rounded-[10px] bg-[#f5f6fb] px-2.5 py-1.5 font-sans">
              <p className="text-[5px] font-semibold tracking-[0.14em] text-[#6b7280] uppercase">{label}</p>
              <p className="text-[7.5px] font-semibold text-[#0b1120]">{value}</p>
            </div>
          ))}
          <span className="rounded-[10px] bg-[#4f46e5] px-3.5 py-2.5 font-sans text-[7px] font-bold text-white">
            Search
          </span>
        </div>
        {/* Asymmetric section head + row cards */}
        <div className="flex items-end justify-between px-5 pt-4 pb-2">
          <p className="text-[11px] font-extrabold tracking-tight text-[#0b1120]">Rooms</p>
          <p className="max-w-[40%] text-right font-sans text-[6px] leading-relaxed text-[#6b7280]">
            Live rates, straight from the desk.
          </p>
        </div>
        <div className="space-y-2 px-5 pb-4">
          {[
            ['Queen Studio', 'City view · 24 m²', '€159', 'tpl-room-1'],
            ['Corner King', 'Wraparound glass · 32 m²', '€209', 'tpl-room-2']
          ].map(([name, meta, price, img]) => (
            <div
              key={name}
              className="grid grid-cols-[1.1fr_2fr_auto] items-center gap-3 overflow-hidden rounded-[16px] bg-white shadow-[0_10px_30px_-18px_rgba(15,23,42,0.4)] ring-1 ring-[#e9ebf3]"
            >
              <Photo src={`${IMG}/${img}.webp`} className="aspect-[4/2.6]" sizes="180px" />
              <div className="font-sans">
                <p className="text-[8.5px] font-bold text-[#0b1120]">{name}</p>
                <p className="text-[6px] text-[#6b7280]">{meta}</p>
              </div>
              <div className="pr-3 text-right font-sans">
                <p className="text-[8.5px] font-bold text-[#4f46e5]">{price}</p>
                <p className="text-[5.5px] text-[#6b7280]">per night</p>
              </div>
            </div>
          ))}
        </div>
        {/* Dark big CTA band */}
        <div className="mx-5 mb-4 flex items-center justify-between rounded-[18px] bg-[#0b1120] px-4 py-3">
          <p className="text-[9px] font-extrabold tracking-tight text-white">
            Book direct. Skip the fees.
          </p>
          <span className="rounded-full bg-[#4f46e5] px-3 py-1.5 font-sans text-[6.5px] font-bold text-white">
            Check availability
          </span>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ============================================================================
 * EDITORIAL - art-directed magazine. Split hero (text panel + photo),
 * numbered kickers, portrait plates, underlined CTAs, ink footer.
 * ========================================================================= */

function EditorialShowcase() {
  return (
    <BrowserFrame domain="theverano.com">
      <div className={`bg-white text-[#2c2620] ${playfair.className}`}>
        {/* Split header */}
        <div className="grid grid-cols-3 items-center border-b border-[#e7e1d8] px-5 py-2.5">
          <div className="flex gap-3 font-sans text-[6px] tracking-[0.2em] text-[#837a70] uppercase">
            <span>Rooms</span>
            <span>Journal</span>
          </div>
          <p className="text-center text-[13px] font-semibold tracking-tight text-[#171310]">
            The Verano
          </p>
          <p className="text-right font-sans text-[6.5px] tracking-[0.16em] text-[#b0573a] uppercase underline decoration-[#b0573a] underline-offset-2">
            Reserve
          </p>
        </div>
        {/* Split hero: text panel + full-height photo */}
        <div className="grid grid-cols-[1.05fr_1fr]">
          <div className="flex flex-col justify-center bg-[#f6f3ee] px-5 py-6">
            <p className="font-sans text-[6px] tracking-[0.26em] text-[#b0573a] uppercase">
              01 &mdash; Welcome
            </p>
            <p className="mt-2 text-[19px] leading-[1.12] font-medium text-[#171310]">
              An old house with new stories to tell.
            </p>
            <p className="mt-2 max-w-[34ch] font-sans text-[6.5px] leading-relaxed text-[#837a70]">
              Nine rooms in a converted press house, a kitchen worth the trip,
              and a courtyard that runs on long afternoons.
            </p>
            <p className="mt-3 font-sans text-[6.5px] tracking-[0.2em] text-[#171310] uppercase underline decoration-[#b0573a] decoration-2 underline-offset-4">
              Plan a stay
            </p>
          </div>
          <Photo src={`${IMG}/tpl-editorial-hero.webp`} className="min-h-full" sizes="320px" />
        </div>
        {/* Numbered rooms section: one large plate + side list */}
        <div className="px-5 pt-4">
          <p className="font-sans text-[6px] tracking-[0.26em] text-[#b0573a] uppercase">
            02 &mdash; The rooms
          </p>
        </div>
        <div className="grid grid-cols-[1.35fr_1fr] gap-3 px-5 pt-2 pb-4">
          <div>
            <Photo src={`${IMG}/tpl-room-3.webp`} className="aspect-[4/3.4]" sizes="340px" />
            <p className="mt-1.5 text-[10px] font-semibold text-[#171310]">The Terrace Room</p>
            <p className="font-sans text-[6px] text-[#837a70]">
              From €210 &mdash; sleeps two, breakfast in the courtyard
            </p>
          </div>
          <div className="flex flex-col justify-between gap-2.5">
            {[
              ['Room No. 4', 'tpl-room-1'],
              ['The Attic', 'tpl-room-2']
            ].map(([name, img]) => (
              <div key={name}>
                <Photo src={`${IMG}/${img}.webp`} className="aspect-[4/2.1]" sizes="240px" />
                <p className="mt-1 text-[8.5px] font-semibold text-[#171310]">{name}</p>
              </div>
            ))}
            <p className="font-sans text-[6px] tracking-[0.18em] text-[#171310] uppercase underline decoration-[#b0573a] underline-offset-2">
              All nine rooms
            </p>
          </div>
        </div>
        {/* Ink footer */}
        <div className="bg-[#171310] px-5 py-2.5 text-center">
          <p className="text-[9px] font-medium text-[#d9d2c8]">The Verano</p>
          <p className="font-sans text-[5.5px] tracking-[0.22em] text-[#837a70] uppercase">
            Rooms &middot; Journal &middot; Kitchen &middot; Directions
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ============================================================================
 * MINIMAL - quiet-luxury gallery. Tracked tiny logo, full-bleed hero with
 * bottom-left caption, no search, borderless square plates, light footer.
 * ========================================================================= */

function MinimalShowcase() {
  return (
    <BrowserFrame domain="haus-hotel.com">
      <div className={`bg-[#fafaf8] text-[#2b2b2b] ${manrope.className}`}>
        {/* Minimal header: logo + burger/CTA only */}
        <div className="flex items-center justify-between border-b border-[#e6e4df] px-5 py-3">
          <p className="text-[8px] font-medium tracking-[0.42em] text-[#0a0a0a] uppercase">Haus</p>
          <div className="flex items-center gap-3">
            <span className="text-[6.5px] tracking-[0.2em] text-[#2b2b2b] uppercase underline underline-offset-2">
              Book
            </span>
            <span className="flex flex-col gap-[2.5px]">
              <span className="block h-px w-3.5 bg-[#0a0a0a]" />
              <span className="block h-px w-3.5 bg-[#0a0a0a]" />
            </span>
          </div>
        </div>
        {/* Full-bleed hero, bottom-left caption */}
        <div className="relative">
          <Photo src={`${IMG}/tpl-minimal-hero.webp`} className="aspect-[16/8]" />
          <div className="absolute bottom-4 left-5">
            <p className="text-[6px] tracking-[0.3em] text-white/90 uppercase">Berlin — Mitte</p>
            <p className="mt-1 text-[14px] font-medium tracking-tight text-white">
              Six rooms. Nothing else.
            </p>
          </div>
        </div>
        {/* Vast whitespace + square borderless plates */}
        <div className="px-5 pt-6 pb-1 text-center">
          <p className="text-[6px] tracking-[0.36em] text-[#9a958f] uppercase">The rooms</p>
        </div>
        <div className="grid grid-cols-3 gap-3 px-5 pt-2 pb-6">
          {[
            ['One', '€180', 'tpl-room-1'],
            ['Two', '€180', 'tpl-room-2'],
            ['Three', '€220', 'tpl-room-3']
          ].map(([name, price, img]) => (
            <div key={name}>
              <Photo src={`${IMG}/${img}.webp`} className="aspect-square" sizes="200px" />
              <div className="mt-1.5 flex items-baseline justify-between border-t border-[#e6e4df] pt-1.5">
                <p className="text-[7.5px] font-medium tracking-[0.14em] text-[#0a0a0a] uppercase">
                  Room {name}
                </p>
                <p className="text-[6.5px] text-[#9a958f]">{price}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Light footer, hairline top */}
        <div className="flex items-center justify-between border-t border-[#e6e4df] px-5 py-3">
          <p className="text-[6px] tracking-[0.36em] text-[#0a0a0a] uppercase">Haus</p>
          <p className="text-[5.5px] tracking-[0.22em] text-[#9a958f] uppercase">
            Rooms &middot; About &middot; Contact
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ============================================================================
 * RESORT - warm coastal escape. Teal topbar, cream glass header, stacked
 * hero (headline band + photo shelf), pill search, chunky cards, teal footer.
 * ========================================================================= */

function ResortShowcase() {
  return (
    <BrowserFrame domain="calasol-resort.com">
      <div className={`bg-[#fff7ef] text-[#324a48] ${sora.className}`}>
        {/* Teal promo topbar */}
        <div className="bg-[#0f3d3e] px-5 py-1 text-center font-sans text-[5.5px] tracking-[0.16em] text-[#cfe0dd] uppercase">
          Summer opening &mdash; 20% off stays of 4+ nights
        </div>
        {/* Cream glassy header */}
        <div className="flex items-center justify-between border-b border-[#f1e3d4] bg-[#fff7ef]/90 px-5 py-2.5">
          <p className="text-[11px] font-extrabold tracking-tight text-[#0f3d3e]">Cala Sol</p>
          <div className="flex items-center gap-3 font-sans text-[6.5px] font-medium text-[#7d8d88]">
            <span>Suites</span>
            <span>Pool &amp; spa</span>
            <span>Dining</span>
            <span className="rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#ff9a3d] px-3 py-1 font-bold text-white shadow-[0_6px_16px_-6px_rgba(255,107,61,0.7)]">
              Book now
            </span>
          </div>
        </div>
        {/* Stacked hero: teal headline band + pill search + photo shelf */}
        <div className="px-5 pt-4 text-center">
          <span className="rounded-full bg-gradient-to-r from-[#ff6b3d]/15 to-[#ffb24a]/20 px-2.5 py-0.5 font-sans text-[5.5px] font-bold tracking-[0.2em] text-[#ff6b3d] uppercase">
            Clifftop &middot; adults only
          </span>
          <p className="mx-auto mt-1.5 max-w-[24ch] text-[17px] leading-tight font-extrabold tracking-tight text-[#0f3d3e]">
            Where the week loses count of itself.
          </p>
        </div>
        <div className="relative z-10 mx-auto mt-2.5 flex w-[86%] items-center gap-1 rounded-full bg-white p-1.5 font-sans shadow-[0_14px_36px_-14px_rgba(15,61,62,0.35)] ring-1 ring-[#f1e3d4]">
          {['12 Jun — 19 Jun', '2 adults', 'Sea view'].map((f) => (
            <span key={f} className="flex-1 rounded-full px-2.5 py-1.5 text-[6.5px] font-semibold text-[#324a48]">
              {f}
            </span>
          ))}
          <span className="rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#ff9a3d] px-3.5 py-1.5 text-[6.5px] font-bold text-white">
            Search
          </span>
        </div>
        <div className="px-5 pt-2.5">
          <Photo src={`${IMG}/tpl-resort-hero.webp`} className="aspect-[16/6.6] rounded-[20px]" />
        </div>
        {/* Chunky rounded room cards with gradient tags */}
        <div className="grid grid-cols-3 gap-2.5 px-5 py-4">
          {[
            ['Sea View Junior', '€310', 'tpl-room-3'],
            ['Garden Pool Suite', '€420', 'tpl-room-2'],
            ['The Lighthouse', '€560', 'tpl-room-1']
          ].map(([name, price, img]) => (
            <div key={name} className="overflow-hidden rounded-[16px] bg-white shadow-[0_12px_30px_-16px_rgba(15,61,62,0.35)]">
              <div className="relative">
                <Photo src={`${IMG}/${img}.webp`} className="aspect-[4/2.7]" sizes="220px" />
                <span className="absolute top-1.5 left-1.5 rounded-full bg-gradient-to-r from-[#ff6b3d] to-[#ffb24a] px-2 py-0.5 font-sans text-[5px] font-bold text-white">
                  From {price}
                </span>
              </div>
              <div className="px-2 py-1.5 font-sans">
                <p className="text-[7.5px] font-bold text-[#0f3d3e]">{name}</p>
                <p className="text-[5.5px] text-[#7d8d88]">Breakfast included</p>
              </div>
            </div>
          ))}
        </div>
        {/* Teal footer */}
        <div className="flex items-center justify-between bg-[#0f3d3e] px-5 py-2.5">
          <p className="text-[9px] font-extrabold tracking-tight text-white">Cala Sol</p>
          <p className="font-sans text-[5.5px] tracking-[0.2em] text-[#cfe0dd] uppercase">
            Suites &middot; Spa &middot; Dining &middot; Getting here
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* ============================================================================
 * FUTURISTIC - dark immersive. Radial mesh on near-black, glass pill nav,
 * gradient-clipped headline, slideshow dots, glass room cards.
 * ========================================================================= */

function FuturisticShowcase() {
  return (
    <BrowserFrame domain="orb-hotel.com" dark>
      <div
        className={`relative text-[#c7d0e6] ${spaceGrotesk.className}`}
        style={{
          background:
            'radial-gradient(60% 50% at 20% 0%, rgba(124,92,255,0.22), transparent 60%), radial-gradient(50% 40% at 85% 10%, rgba(77,208,255,0.16), transparent 60%), #070a16'
        }}
      >
        {/* Floating glass pill nav */}
        <div className="px-5 pt-3">
          <div className="flex items-center justify-between rounded-full border border-white/10 bg-white/[0.045] px-3.5 py-2 backdrop-blur">
            <p className="text-[10px] font-bold tracking-[0.14em] text-white">ØRB</p>
            <div className="flex items-center gap-3 font-sans text-[6px] font-medium text-[#8b96b8]">
              <span>Rooms</span>
              <span>Rooftop</span>
              <span>Lab</span>
              <span className="rounded-full bg-gradient-to-r from-[#7c5cff] to-[#4dd0ff] px-2.5 py-1 font-bold text-[#07091a] shadow-[0_0_18px_rgba(124,92,255,0.55)]">
                Book
              </span>
            </div>
          </div>
        </div>
        {/* Slideshow hero with gradient headline + dots */}
        <div className="px-5 pt-3">
          <div className="relative overflow-hidden rounded-[16px] border border-white/10">
            <Photo src={`${IMG}/tpl-futuristic-hero.webp`} className="aspect-[16/7.2]" />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#070a16]/35 px-6 text-center">
              <p className={`text-[5.5px] tracking-[0.3em] text-[#6ee7ff] uppercase ${jetbrainsMono.className}`}>
                023 rooms — one skyline
              </p>
              <p className="mt-1.5 max-w-[22ch] bg-gradient-to-r from-[#b7a3ff] via-white to-[#8fe6ff] bg-clip-text text-[17px] leading-tight font-bold tracking-tight text-transparent">
                Check in to the night.
              </p>
              <span className="mt-2.5 rounded-full bg-gradient-to-r from-[#7c5cff] to-[#4dd0ff] px-3.5 py-1.5 font-sans text-[6.5px] font-bold text-[#07091a]">
                Explore rooms
              </span>
            </div>
            <div className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 gap-1">
              <span className="h-1 w-3.5 rounded-full bg-[#6ee7ff]" />
              <span className="size-1 rounded-full bg-white/30" />
              <span className="size-1 rounded-full bg-white/30" />
            </div>
          </div>
        </div>
        {/* Glass room card carousel */}
        <div className={`flex items-baseline justify-between px-5 pt-4 pb-2 ${jetbrainsMono.className}`}>
          <p className="text-[6px] tracking-[0.28em] text-[#6ee7ff] uppercase">/ rooms</p>
          <p className="text-[5.5px] text-[#8b96b8]">drag →</p>
        </div>
        <div className="flex gap-2.5 overflow-hidden px-5 pb-4">
          {[
            ['Pod King', '€190', 'tpl-room-1', false],
            ['Skyline Loft', '€290', 'tpl-room-2', true],
            ['The Observatory', '€480', 'tpl-room-3', false]
          ].map(([name, price, img, glow]) => (
            <div
              key={name}
              className={`w-[38%] flex-shrink-0 overflow-hidden rounded-[14px] border bg-white/[0.045] backdrop-blur ${
                glow
                  ? 'border-[#7c5cff]/60 shadow-[0_0_26px_-4px_rgba(124,92,255,0.5)]'
                  : 'border-white/10'
              }`}
            >
              <Photo src={`${IMG}/${img}.webp`} className="aspect-[4/2.5]" sizes="220px" />
              <div className="flex items-center justify-between px-2 py-1.5 font-sans">
                <p className="text-[7px] font-bold text-white">{name}</p>
                <p className="text-[6.5px] font-bold text-[#6ee7ff]">{price}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Footer */}
        <div className="flex items-center justify-between border-t border-white/10 bg-[#04060f] px-5 py-2.5">
          <p className="text-[9px] font-bold tracking-[0.14em] text-white">ØRB</p>
          <p className={`text-[5px] tracking-[0.24em] text-[#8b96b8] uppercase ${jetbrainsMono.className}`}>
            Rooms / Rooftop / Lab / Map
          </p>
        </div>
      </div>
    </BrowserFrame>
  );
}

/* Public API ------------------------------------------------------------------ */

export const TEMPLATE_SHOWCASES = {
  classic: ClassicShowcase,
  modern: ModernShowcase,
  editorial: EditorialShowcase,
  minimal: MinimalShowcase,
  resort: ResortShowcase,
  futuristic: FuturisticShowcase
};

export default function TemplateShowcase({slug}) {
  const Showcase = TEMPLATE_SHOWCASES[slug];
  if (!Showcase) return null;
  return <Showcase />;
}
